import { useState } from "react";
import { db } from "../../../../util/firebase";
import {
  query,
  collection,
  getDocs,
  where,
  doc,
  updateDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { FaChevronLeft } from "react-icons/fa";
import { Button } from "reactstrap";
import BarLoader from "react-spinners/BarLoader";
import Swal from "sweetalert2";

function RecoverCorruptedGroups() {
  const [isLoading, setIsLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [recoveryResults, setRecoveryResults] = useState(null);
  const navigate = useNavigate();

  const analyzeCorruption = async () => {
    try {
      setIsLoading(true);

      const q = query(
        collection(db, "groups"),
        where("league", "==", "Liga Mayor de Futbol Salvadoreño"),
        where("tournament", "==", "Noviembre - 2025")
      );
      const docs = await getDocs(q);

      // Group by tournament type
      const byType = {
        casual: [],
        standard: [],
        highStakes: [],
        vipExclusive: [],
      };

      docs.forEach((docSnap) => {
        const data = { ...docSnap.data(), docId: docSnap.id };
        const type = (
          data.tournamentType ||
          data.tournamenttype ||
          "casual"
        ).toLowerCase();
        const normalizedType =
          type === "highstakes"
            ? "highStakes"
            : type === "vipexclusive"
            ? "vipExclusive"
            : type;
        if (byType[normalizedType]) {
          byType[normalizedType].push(data);
        }
      });

      // Use vipExclusive as the template (it has correct names)
      const template = byType.vipExclusive;

      const analysisData = {
        vipExclusive: template.length,
        standard: byType.standard.length,
        highStakes: byType.highStakes.length,
        casual: byType.casual.length,
        templateGroups: template.map((g) => g.name).sort(),
        highStakesGroups: byType.highStakes.map((g) => g.name),
        casualGroups: byType.casual.map((g) => g.name),
        corruptedHighStakes: byType.highStakes,
        corruptedCasual: byType.casual,
        templateData: template,
        allGroups: byType,
      };

      setAnalysis(analysisData);
      setIsLoading(false);
    } catch (error) {
      console.error("Error analyzing groups:", error);
      alert("Error analyzing groups");
      setIsLoading(false);
    }
  };

  const recoverGroups = async () => {
    const result = await Swal.fire({
      title: "Recover Corrupted Groups?",
      html: `This will:<br/>
        1. Map each corrupted group to its correct template from vipExclusive<br/>
        2. Update names and events while preserving UIDs<br/>
        3. Keep all other fields intact<br/><br/>
        <strong>This cannot be undone!</strong>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "YES, RECOVER",
      cancelButtonText: "CANCEL",
    });

    if (!result.isConfirmed) return;

    try {
      setIsLoading(true);
      const results = {
        updated: { highStakes: 0, casual: 0 },
        errors: [],
        mappings: [],
      };

      // Sort template groups by dateCreated to match chronological order
      const templateGroups = [...analysis.templateData].sort(
        (a, b) => new Date(a.dateCreated) - new Date(b.dateCreated)
      );

      // Sort corrupted groups by dateCreated to match them in order
      const highStakesGroups = [...analysis.corruptedHighStakes].sort(
        (a, b) => new Date(a.dateCreated) - new Date(b.dateCreated)
      );
      const casualGroups = [...analysis.corruptedCasual].sort(
        (a, b) => new Date(a.dateCreated) - new Date(b.dateCreated)
      );

      // Update highStakes groups
      for (
        let i = 0;
        i < highStakesGroups.length && i < templateGroups.length;
        i++
      ) {
        const corruptedGroup = highStakesGroups[i];
        const template = templateGroups[i];

        try {
          const groupRef = doc(db, "groups", corruptedGroup.docId);
          await updateDoc(groupRef, {
            name: template.name,
            events: template.events || [],
          });
          results.updated.highStakes++;
          results.mappings.push({
            type: "highStakes",
            uid: corruptedGroup.uid.substring(0, 8),
            oldName: corruptedGroup.name,
            newName: template.name,
          });
        } catch (err) {
          results.errors.push(
            `Failed to update highStakes ${corruptedGroup.uid}: ${err.message}`
          );
        }
      }

      // Update casual groups
      for (
        let i = 0;
        i < casualGroups.length && i < templateGroups.length;
        i++
      ) {
        const corruptedGroup = casualGroups[i];
        const template = templateGroups[i];

        try {
          const groupRef = doc(db, "groups", corruptedGroup.docId);
          await updateDoc(groupRef, {
            name: template.name,
            events: template.events || [],
          });
          results.updated.casual++;
          results.mappings.push({
            type: "casual",
            uid: corruptedGroup.uid.substring(0, 8),
            oldName: corruptedGroup.name,
            newName: template.name,
          });
        } catch (err) {
          results.errors.push(
            `Failed to update casual ${corruptedGroup.uid}: ${err.message}`
          );
        }
      }

      setRecoveryResults(results);

      await Swal.fire({
        title: "Recovery Complete!",
        html: `
          <strong>Updated:</strong><br/>
          - highStakes: ${results.updated.highStakes} groups<br/>
          - casual: ${results.updated.casual} groups<br/><br/>
          ${
            results.errors.length > 0
              ? `<strong>Errors:</strong><br/>${results.errors.join("<br/>")}`
              : ""
          }
        `,
        icon: results.errors.length > 0 ? "warning" : "success",
        confirmButtonColor: "#005EE1",
      });

      setIsLoading(false);
    } catch (error) {
      console.error("Error recovering groups:", error);
      Swal.fire({
        title: "Error",
        text: "An error occurred during recovery. Please try again.",
        icon: "error",
        confirmButtonColor: "#005EE1",
      });
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="loadingPage">
        <img
          alt="logo"
          src="https://firebasestorage.googleapis.com/v0/b/sport-league-e35d7.appspot.com/o/assets%2Flogos%2F01%20-%20SL%20Logo%203.png?alt=media&token=2e5a6320-d2d7-45f8-9bc9-a8f9c7f8caa6"
          style={{ width: 100, marginBottom: "1rem" }}
        />
        <BarLoader color="#005EE1" />
      </div>
    );
  }

  return (
    <div id="newsArticlePage" className="page">
      <h1>
        <FaChevronLeft
          onClick={() => navigate(-1)}
          style={{
            color: "#005EE1",
            width: 35,
            height: 35,
            marginTop: -8,
            marginRight: "0.25rem",
            cursor: "pointer",
          }}
        />
        RECOVER CORRUPTED GROUPS
      </h1>

      <div className="pageContent">
        <p style={{ marginBottom: "1rem" }}>
          This tool will recover the corrupted highStakes and casual groups for
          <strong> Liga Mayor de Futbol Salvadoreño - Noviembre - 2025</strong>.
        </p>

        {!analysis && (
          <Button
            onClick={analyzeCorruption}
            className="primaryBtn"
            style={{ marginBottom: "1rem" }}
          >
            ANALYZE CORRUPTION
          </Button>
        )}

        {analysis && !recoveryResults && (
          <div style={{ marginTop: "2rem" }}>
            <h3>Analysis Results</h3>
            <div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
              <p>
                <strong>Group counts:</strong>
              </p>
              <ul>
                <li>vipExclusive: {analysis.vipExclusive} groups ✅</li>
                <li>standard: {analysis.standard} groups ✅</li>
                <li>highStakes: {analysis.highStakes} groups (corrupted) ❌</li>
                <li>casual: {analysis.casual} groups (corrupted) ❌</li>
              </ul>
            </div>

            <div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
              <p>
                <strong>Template (vipExclusive) group names:</strong>
              </p>
              <ul>
                {analysis.templateGroups.map((name, i) => (
                  <li key={i}>{name}</li>
                ))}
              </ul>
            </div>

            <div style={{ marginTop: "1rem", marginBottom: "2rem" }}>
              <p>
                <strong>Corrupted data:</strong>
              </p>
              <p>highStakes: All named "{analysis.highStakesGroups[0]}"</p>
              <p>casual: All named "{analysis.casualGroups[0]}"</p>
            </div>

            <Button
              onClick={recoverGroups}
              className="primaryBtn"
              style={{ marginRight: "1rem" }}
            >
              DELETE CORRUPTED GROUPS
            </Button>
            <Button onClick={() => setAnalysis(null)} className="secondaryBtn">
              CANCEL
            </Button>
          </div>
        )}

        {recoveryResults && (
          <div style={{ marginTop: "2rem" }}>
            <h3>Recovery Complete</h3>
            <div style={{ marginTop: "1rem" }}>
              <p>
                <strong>Updated Groups:</strong>
              </p>
              <ul>
                <li>highStakes: {recoveryResults.updated.highStakes}</li>
                <li>casual: {recoveryResults.updated.casual}</li>
              </ul>
              {recoveryResults.errors.length > 0 && (
                <>
                  <p style={{ color: "red", marginTop: "1rem" }}>
                    <strong>Errors:</strong>
                  </p>
                  <ul>
                    {recoveryResults.errors.map((err, i) => (
                      <li key={i} style={{ color: "red" }}>
                        {err}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            <div style={{ marginTop: "2rem" }}>
              <p>
                <strong>Mappings Applied:</strong>
              </p>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  marginTop: "1rem",
                }}
              >
                <thead>
                  <tr style={{ borderBottom: "2px solid #ddd" }}>
                    <th style={{ padding: "0.5rem", textAlign: "left" }}>
                      Type
                    </th>
                    <th style={{ padding: "0.5rem", textAlign: "left" }}>
                      UID
                    </th>
                    <th style={{ padding: "0.5rem", textAlign: "left" }}>
                      Old Name
                    </th>
                    <th style={{ padding: "0.5rem", textAlign: "left" }}>
                      New Name
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {recoveryResults.mappings.map((mapping, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #eee" }}>
                      <td style={{ padding: "0.5rem" }}>{mapping.type}</td>
                      <td
                        style={{ padding: "0.5rem", fontFamily: "monospace" }}
                      >
                        {mapping.uid}...
                      </td>
                      <td style={{ padding: "0.5rem", color: "#d33" }}>
                        {mapping.oldName}
                      </td>
                      <td style={{ padding: "0.5rem", color: "#28a745" }}>
                        {mapping.newName}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div
              style={{
                marginTop: "2rem",
                padding: "1rem",
                backgroundColor: "#d4edda",
                border: "1px solid #28a745",
                borderRadius: "4px",
              }}
            >
              <p>
                <strong>✅ Recovery Successful!</strong>
              </p>
              <p>
                All groups have been corrected with proper names and events
                while preserving their UIDs.
              </p>
            </div>
            <Button
              onClick={() => navigate("/admin")}
              className="primaryBtn"
              style={{ marginTop: "1rem", marginRight: "0.5rem" }}
            >
              GO TO ADMIN
            </Button>
            <Button
              onClick={() => window.location.reload()}
              className="secondaryBtn"
              style={{ marginTop: "1rem" }}
            >
              REFRESH PAGE
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default RecoverCorruptedGroups;
